import React from 'react';

import { getEnv } from '../../../../lib/utils/getEnv';

const { VITE_HELP_CENTRE_URL } = getEnv();

export const faqsConfig = [
  {
    question: 'What makes Pata the best booking platform for your pet care business?',
    answer: () => (
      <>
        <p>
          Pata is the UK’s fastest-growing booking platform built exclusively for pet care professionals, from dog
          groomers and trainers to walkers and many others. Whether you're just starting out or running an established
          business, Pata gives you everything you need to manage bookings, minimise the impact of no-shows or late
          cancellations, and attract new clients online – all in one place. Our booking software simplifies your
          day-to-day admin and helps you take bookings 24/7 via your own profile on the Pata marketplace.
        </p>

        <p>
          There are no monthly subscription fees for Pata booking platform – you only pay a small processing fee when
          you get paid, either online or via a PataPay card reader.
        </p>
      </>
    ),
  },
  {
    question: 'How can Pata help my pet care business?',
    answer: () => (
      <>
        <p>
          Pata is more than just a booking platform; it’s your all-in-one toolkit for running and growing a successful
          pet care business. Whether you’re a dog groomer, trainer, walker, sitter, vet, boarder or pet hydrotherapist,
          our booking software is designed to make your day-to-day operations easier and more efficient.
        </p>

        <p>
          Reduce no-shows and last-minute cancellations for your pet care business with upfront payments, waiting lists
          and our smart slot filler feature, which automatically fills any late cancellations in your schedule to keep
          your diary full.
        </p>

        <p>
          Start accepting online bookings 24/7 and showcase your pet business on the Pata marketplace (perfect if your
          pet care business doesn't have a website!). Add pet service descriptions, pricing, photos of your work and
          start collecting reviews from clients to maximise results.
        </p>

        <p>
          Every booking is fully visible in your digital calendar on the Pata platform, and all your admin can be viewed
          in one place – check out pet profiles, client contact details, appointment history and transactions at a
          glance.
        </p>

        <p>
          Whether you're just starting out or running a busy multi-location business, Pata booking platform helps you
          save time on admin and marketing, so you can focus on what you love most – caring for pets.
        </p>
      </>
    ),
  },
  {
    question: 'Do I need to be a mature pet care business to join Pata?',
    answer: () => (
      <>
        <p>
          Not at all! Whether you're a brand-new solo dog walker or a nationwide grooming salon with multiple locations,
          Pata is built to support pet care businesses of every size and shape.
        </p>

        <p>
          Our booking platform is completely free to join, so smaller businesses can enjoy the same powerful tools and
          visibility as bigger players on the market. From managing appointments to reducing no-shows, you’ll have
          everything you need to grow and maintain your pet care business. Larger companies can even manage multiple
          branches from a single account; it’s all about making life easier, no matter where you are in your pet care
          business journey.
        </p>
      </>
    ),
  },
  {
    question: 'How do I start using PataPay?',
    answer: () => (
      <>
        <p>
          PataPay allows you to take secure online payments and in-person transactions, helping you reduce no-shows, get
          paid faster, and manage bookings more efficiently.
        </p>

        <p>
          Whether you're an individual pet care provider, a registered business, or a non-profit, you’ll find everything
          you need to get set up. Once you've created your account on the Pata booking platform, simply head to the
          Payments section (look for the credit card icon in the side menu), click Set Up Payments, and follow the
          step-by-step instructions on screen.
        </p>
        <p>
          For full details on the required documents and further steps to manage PataPay for your pet care business,
          visit our{' '}
          <a href={`${VITE_HELP_CENTRE_URL}`} target="_blank">
            Help Centre
          </a>
          .
        </p>
      </>
    ),
  },
  {
    question: 'How fast are the payouts?',
    answer: () => (
      <>
        <p>Pata offers fast and secure payouts to support your pet care business.</p>

        <p>
          Your first payout may take 7–14 calendar days to reach your bank account, as part of the initial verification
          process. After that, you’re in control: set your preferred payout schedule, and funds from online or in-person
          payments will typically arrive within 5–7 calendar days, depending on your selected settings.
        </p>
        <p>
          With PataPay, you can accept payments confidently and get paid quickly, helping you manage your pet care
          bookings with ease.
        </p>
      </>
    ),
  },
  {
    question:
      'Can PataPay help me to secure deposits and protect my pet care business from no-shows and late cancellations?',
    answer: () => (
      <>
        <p>
          Securing deposits is a proven way to reduce no-shows and last-minute cancellations – and with Pata booking
          platform, it’s easy to set up. Once you've enabled PataPay, you can start requesting upfront payments or
          deposits for your pet care services.
        </p>

        <p>
          Set a custom deposit amount for each service, which by default is non-refundable*, and collect payment card
          details for future no-show fees. This adds an extra layer of commitment from clients and protects your time.
        </p>
        <p>
          Pata also offers other tools to help your pet business minimise missed appointments, including automated
          reminders, waiting lists, and our smart slot filler technology to keep your schedule full.
        </p>

        <p>
          For step-by-step instructions on setting up deposits, visit our{' '}
          <a href={`${VITE_HELP_CENTRE_URL}`} target="_blank">
            Help Centre
          </a>
          .
        </p>
      </>
    ),
  },
  {
    question: 'How do I place an order for the PataPay card reader? ',
    answer: () => (
      <>
        <p>
          The PataPay card reader is a fast, secure way for pet care professionals to accept card payments from clients
          – perfect for pet groomers, dog trainers, walkers, and other pet service providers.
        </p>

        <p>
          To order your PataPay card reader, simply log into your account on the Pata booking platform and go to the
          Payments section (look for the credit card icon). First, sign up for PataPay. Then either select Terminal from
          the menu and click Request Now, or scroll to the Terminal section and tap Request Now directly.
        </p>

        <p>
          You’ll be asked to enter your payment details and delivery address. Once payment is complete, you’ll receive
          an order confirmation by email.
        </p>
        <p>Please allow up to 10 working days for your card reader to arrive.</p>
        <p>With PataPay, accepting in-person payments for your pet care services has never been easier!</p>
      </>
    ),
  },
  {
    question: 'What are the transaction fees for using PataPay?',
    answer: () => (
      <>
        <p>
          PataPay keeps things simple with transparent, pay-as-you-go pricing – no monthly fees or hidden charges. There
          are two types of payment methods available through the Pata booking platform, each with its own processing
          fee:
        </p>

        <p>Online payments: 2.99% + £0.45* per transaction</p>

        <p>Card reader payments (in-person): 1.75% + £0.45* per transaction</p>

        <p>Card collection: £0.25* per card collection</p>

        <p>
          These transaction fees help cover secure payment processing and support services. You only pay when you get
          paid – perfect for pet care professionals who want flexibility without ongoing costs.
        </p>
        <p>*Fees are subject to VAT where applicable.</p>
      </>
    ),
  },
];
